import { useState } from 'react';
import { useQueryFetch } from 'sonata-react-components';
import { DoohAudienceProfilingStatus } from 'sonata-static-storage/lib/schemas/doohReport/doohAudienceProfiling.schema';

import AudienceProfiling from '@/components/doohReport/AudienceProfiling/AudienceProfiling';
import ContextEvents from '@/components/doohReport/ContextEvents/ContextEvents';
import ReportHeader from '@/components/doohReport/common/ReportHeader/ReportHeader';
import DeliveryDrivers from '@/components/doohReport/DeliveryDrivers/DeliveryDrivers';
import ReachFrequency from '@/components/doohReport/ReachFrequency/ReachFrequency';
import ReportFooter from '@/components/doohReport/ReportFooter/ReportFooter';
import ReportSummaryCard from '@/components/doohReport/ReportSummaryCard/ReportSummaryCard';
import ReportUnavailable from '@/components/doohReport/ReportUnavailable/ReportUnavailable';
import { useDoohAudienceProfilingSocket } from '@/hooks/useDoohAudienceProfilingSocket';
import { getDoohAudienceProfiling, getDoohReport } from '@/services/doohReport.service';
import DoohReportSkeleton from './DoohReportSkeleton';

import type {
  DoohAudienceProfilingResponse,
  DoohReportResponse
} from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';
import type { DoohAudienceProfilingOutputData } from 'sonata-static-storage/lib/schemas/doohReport/doohAudienceProfiling.schema';

import './doohReport.scss';

type Props = {
  lineItemId: string;
  userId: string;
};

export default function DoohReport({ lineItemId, userId }: Readonly<Props>) {
  const { data: report, isLoading } = useQueryFetch<DoohReportResponse | null>(
    ['doohReport-report', lineItemId, userId],
    async () => await getDoohReport(lineItemId, userId)
  );

  const [profilingWsData, setProfilingWsData] = useState<DoohAudienceProfilingOutputData | null>(null);
  const { data: profilingQuery, isLoading: isProfilingLoading } = useQueryFetch<DoohAudienceProfilingResponse>(
    ['doohReport', 'audienceProfiling', lineItemId, userId],
    () => getDoohAudienceProfiling(lineItemId, userId),
    { enabled: !!report?.available }
  );

  const profilingStatus = profilingWsData !== null ? DoohAudienceProfilingStatus.processed : profilingQuery?.status;
  const profilingData = profilingWsData ?? profilingQuery?.data ?? null;
  const profilingWeek =
    profilingStatus === DoohAudienceProfilingStatus.processing ? (profilingQuery?.week ?? null) : null;

  useDoohAudienceProfilingSocket({ lineItemId, onReady: setProfilingWsData, week: profilingWeek });

  if (isLoading || !report) {
    return (
      <div className="doohReport">
        <div className="doohReport__content">
          <DoohReportSkeleton />
        </div>
      </div>
    );
  }

  if (!report.available || !report.metrics || !report.audience) {
    return (
      <div className="doohReport">
        <ReportUnavailable availableFrom={report.availableFrom} />
      </div>
    );
  }

  return (
    <div className="doohReport">
      <ReportHeader lineItem={report.lineItem} />
      <div className="doohReport__content">
        <ReportSummaryCard
          audience={report.audience}
          isSummaryLoading={isProfilingLoading || profilingStatus === DoohAudienceProfilingStatus.processing}
          lineItem={report.lineItem}
          metrics={report.metrics}
          summary={profilingData?.summary ?? null}
        />
        <AudienceProfiling
          data={profilingData}
          isLoading={isProfilingLoading}
          sectionNumber="01"
          status={profilingStatus}
        />
        <DeliveryDrivers screens={report.screens ?? []} sectionNumber="02" />
        <ContextEvents
          baselineOnTargetPct={report.audience.baselineOnTargetPct}
          campaignOta={report.metrics.ota}
          events={report.events ?? []}
          sectionNumber="03"
        />
        <ReachFrequency
          audience={report.audience}
          dailyCumulative={report.dailyCumulative}
          events={report.events ?? []}
          metrics={report.metrics}
          sectionNumber="04"
        />
        {report.lastUpdatedAt && <ReportFooter lastUpdatedAt={report.lastUpdatedAt} />}
      </div>
    </div>
  );
}
